import { User } from "features/user";
import { SocialListeningPermission } from "features/user/types";
import { entitlementsPermission } from "./entitlements-permission";

class SlPermission {
  private socialListeningPermissionKeys: Array<
    keyof SocialListeningPermission
  > = [
    "hasSentiment",
    "hasAudience",
    "hasCompetitorView",
    "hasCompetitorEdit",
    "hasKeywords",
  ];

  isCollab = (user: User) => {
    return user.role === "collaborator";
  };

  enabledSocialListening = (user: User) => {
    return this.isCollab(user)
      ? this.hasSocialListening(user)
      : entitlementsPermission.hasSocialListening(user);
  };

  private requirePermissionIfCollab(
    user: User,
    key: keyof SocialListeningPermission
  ) {
    return this.isCollab(user)
      ? (user.preferences?.socialListeningPermission?.[key] ?? true)
      : true;
  }

  hasSocialListeningKeyword = (user: User) => {
    return (
      this.requirePermissionIfCollab(user, "hasKeywords") &&
      entitlementsPermission.hasSocialListeningKeyword(user)
    );
  };

  hasSocialListeningCompetitors = (user: User) => {
    return (
      this.requirePermissionIfCollab(user, "hasCompetitorView") &&
      entitlementsPermission.hasSocialListeningCompetitors(user)
    );
  };

  hasSocialListeningCompetitorsEdit = (user: User) => {
    return (
      this.requirePermissionIfCollab(user, "hasCompetitorEdit") &&
      entitlementsPermission.hasSocialListeningCompetitors(user)
    );
  };

  hasSocialListeningSentiment = (user: User) => {
    return (
      this.requirePermissionIfCollab(user, "hasSentiment") &&
      entitlementsPermission.hasSocialListeningSentiment(user)
    );
  };

  hasSocialListeningAudience = (user: User) => {
    return (
      this.requirePermissionIfCollab(user, "hasAudience") &&
      entitlementsPermission.hasSocialListeningAudience(user)
    );
  };

  hasSocialListening = (user: User) => {
    return (
      this.hasSocialListeningKeyword(user) ||
      this.hasSocialListeningAudience(user) ||
      this.hasSocialListeningSentiment(user) ||
      this.hasSocialListeningCompetitors(user)
    );
  };

  hasRestrictedSocialListeningAccess = (user?: User) => {
    if (!user || !this.isCollab(user)) {
      return false;
    }

    const hasEntitlementRestriction =
      (entitlementsPermission.hasSocialListeningKeyword(user) &&
        !this.hasSocialListeningKeyword(user)) ||
      (entitlementsPermission.hasSocialListeningAudience(user) &&
        !this.hasSocialListeningAudience(user)) ||
      (entitlementsPermission.hasSocialListeningSentiment(user) &&
        !this.hasSocialListeningSentiment(user)) ||
      (entitlementsPermission.hasSocialListeningCompetitors(user) &&
        !this.hasSocialListeningCompetitors(user));

    if (hasEntitlementRestriction) {
      return true;
    }

    const socialListeningPermission =
      user.preferences?.socialListeningPermission;
    const hasExplicitRestriction = this.socialListeningPermissionKeys.some(
      (key) => socialListeningPermission?.[key] === false
    );

    return hasExplicitRestriction;
  };

  getSocialListeningUnavailableRoute = (basePath: string, user?: User) => {
    return `${basePath}/${
      this.hasRestrictedSocialListeningAccess(user) ? "no-access" : "upgrade"
    }`;
  };

  getMissingEntitlements = (user: User) => {
    const missingEntitlements = [];

    if (!this.hasSocialListeningKeyword(user)) {
      missingEntitlements.push("listening-keyword");
    }

    if (!this.hasSocialListeningAudience(user)) {
      missingEntitlements.push("listening-audience");
    }

    if (!this.hasSocialListeningSentiment(user)) {
      missingEntitlements.push("listening-sentiment");
    }

    if (!this.hasSocialListeningCompetitors(user)) {
      missingEntitlements.push("listening-competitors");
    }

    return missingEntitlements;
  };
}

export const slPermission = new SlPermission();
